import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchOverviewSiteConditionPosture, type OverviewDecisionSummary } from '../domain/continuousSafety/overviewSiteConditionSummary';
import { DEFAULT_OVER_TIME_WINDOW, type OverTimeWindowId } from '../domain/sellableSiteExperience/overTimePresenter';
import { msUntilNextOpenSitePt15mBoundary, openSitePt15mWindowStart } from '../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { ensureUiScope } from '../services/api';
import { reportsApi } from '../services/reportsApi';
import { OverviewV2AreaCentricView } from './OverviewV2AreaCentricView';
import {
  attachOverviewV2RecordedControls,
  attachOverviewV2SafetyCaseIds,
  buildOverviewV2PatternKey,
  type OverviewV2WorkflowIdentity,
} from './overviewV2Binding';

type OverviewReadState = 'authoritative' | 'unavailable' | 'partial' | 'stale';

type LoadState = {
  status: 'loading' | 'ready' | 'empty' | 'error' | 'stale';
  summary: OverviewDecisionSummary | null;
  message: string | null;
  scopeKey: string;
  lastSuccessfulReadAt: string | null;
  readState: OverviewReadState;
};

const OVERVIEW_UNAVAILABLE_NOTE = 'Condition-state Overview is unavailable. Incident-pipeline health does not confirm this Overview.';

function overviewScopeKey(siteId: string): string {
  const scope = ensureUiScope();
  return `${String(scope.orgId || '').trim()}|${siteId}`;
}

function resolveV2SiteId(search: string): string {
  const params = new URLSearchParams(search);
  return String(params.get('site') || params.get('site_id') || ensureUiScope().siteId || '').trim();
}

/** Re-read Current Activity when the already-open site-local PT15M window rolls. */
function useOpenSitePt15mWindowStart(timeZone?: string | null): string | null {
  const [windowStart, setWindowStart] = useState<string | null>(
    () => (timeZone ? openSitePt15mWindowStart(new Date(), timeZone) : null),
  );
  useEffect(() => {
    if (!timeZone) {
      setWindowStart(null);
      return undefined;
    }
    let timeoutId = 0;
    let cancelled = false;
    const publish = () => {
      setWindowStart(openSitePt15mWindowStart(new Date(), timeZone));
    };
    publish();
    const schedule = () => {
      const delay = msUntilNextOpenSitePt15mBoundary(new Date(), timeZone);
      const waitMs = delay == null ? 15_000 : Math.max(250, delay);
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        publish();
        schedule();
      }, waitMs);
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [timeZone]);
  return windowStart;
}

function applyOverviewFetchResult(
  result: Awaited<ReturnType<typeof fetchOverviewSiteConditionPosture>>,
  setLoadState: React.Dispatch<React.SetStateAction<LoadState>>,
  siteId: string,
) {
  const scopeKey = overviewScopeKey(siteId);
  if (result.status === 'success') {
    const partial = result.data.siteTotalsAuthoritative === false
      || result.data.readCompleteness === 'partial';
    setLoadState({
      status: 'ready',
      summary: result.data,
      message: partial
        ? 'Some safety conditions could not be read. Site-wide totals are withheld.'
        : null,
      scopeKey,
      lastSuccessfulReadAt: new Date().toISOString(),
      readState: partial ? 'partial' : 'authoritative',
    });
    return;
  }
  setLoadState((current) => {
    if (current.scopeKey === scopeKey && current.summary && current.lastSuccessfulReadAt) {
      return {
        status: 'stale',
        summary: current.summary,
        message: `STALE / UNVERIFIED. Last successful read ${current.lastSuccessfulReadAt}. These figures are not live, current, or fully covered measurements.`,
        scopeKey,
        lastSuccessfulReadAt: current.lastSuccessfulReadAt,
        readState: 'stale',
      };
    }
    if (result.status === 'disabled') {
      return {
        status: 'empty',
        summary: null,
        message: 'Condition-state Overview is disabled for this site.',
        scopeKey,
        lastSuccessfulReadAt: null,
        readState: 'unavailable',
      };
    }
    const detail = result.status === 'error'
      ? (result.message || 'Condition-state data could not be loaded for this site.')
      : 'Condition-state data could not be loaded for this site.';
    return {
      status: 'error',
      summary: null,
      message: `${detail} ${OVERVIEW_UNAVAILABLE_NOTE}`,
      scopeKey,
      lastSuccessfulReadAt: null,
      readState: 'unavailable',
    };
  });
}

function identitiesFromSummary(summary: OverviewDecisionSummary | null): OverviewV2WorkflowIdentity[] {
  if (!summary) return [];
  const rows = [...(summary.now.conditionRows || []), ...(summary.overTime.conditionRows || [])];
  const seen = new Set<string>();
  const identities: OverviewV2WorkflowIdentity[] = [];
  rows.forEach((row) => {
    if (seen.has(row.conditionId)) return;
    seen.add(row.conditionId);
    identities.push({
      conditionId: row.conditionId,
      patternKey: buildOverviewV2PatternKey({
        incidentType: row.incidentType,
        zoneId: row.zoneId,
        cameraId: row.cameraId,
        deviceId: row.deviceId,
      }) || undefined,
    });
  });
  return identities;
}

const AcceptanceOverviewV2Page: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const siteId = useMemo(() => resolveV2SiteId(location.search), [location.search]);
  const loadedSiteTimeZone = useSiteTimeZone(siteId);
  const [overTimeWindowId, setOverTimeWindowId] = useState<OverTimeWindowId>(DEFAULT_OVER_TIME_WINDOW);
  const [loadState, setLoadState] = useState<LoadState>({
    status: 'loading',
    summary: null,
    message: null,
    scopeKey: '',
    lastSuccessfulReadAt: null,
    readState: 'unavailable',
  });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [workflowIdentities, setWorkflowIdentities] = useState<OverviewV2WorkflowIdentity[]>([]);
  const siteTimeZone = loadState.summary?.siteTimezone || loadedSiteTimeZone;
  const openPt15mWindowStart = useOpenSitePt15mWindowStart(siteTimeZone);
  const lastFetchedPt15mWindowRef = useRef<string | null>(null);

  useEffect(() => {
    lastFetchedPt15mWindowRef.current = null;
  }, [siteId]);

  useEffect(() => {
    let active = true;
    if (!siteId) {
      setLoadState({
        status: 'empty',
        summary: null,
        message: 'Select a site to load Overview V2.',
        scopeKey: '',
        lastSuccessfulReadAt: null,
        readState: 'unavailable',
      });
      return () => {
        active = false;
      };
    }

    const scopeKey = overviewScopeKey(siteId);
    setLoadState((current) => {
      const sameScope = current.scopeKey === scopeKey;
      return {
        status: 'loading',
        summary: sameScope ? current.summary : null,
        message: null,
        scopeKey,
        lastSuccessfulReadAt: sameScope ? current.lastSuccessfulReadAt : null,
        readState: sameScope ? current.readState : 'unavailable',
      };
    });
    void fetchOverviewSiteConditionPosture(siteId, {
      selectedOverTimeWindowId: overTimeWindowId,
    }).then((result) => {
      if (!active) return;
      applyOverviewFetchResult(result, setLoadState, siteId);
    }).catch(() => {
      if (!active) return;
      applyOverviewFetchResult({
        status: 'error',
        httpStatus: null,
        message: 'Condition-state data could not be loaded for this site.',
      }, setLoadState, siteId);
    });

    return () => {
      active = false;
    };
  }, [siteId, overTimeWindowId, reloadNonce]);

  useEffect(() => {
    if (!siteId || !openPt15mWindowStart) return undefined;
    const previous = lastFetchedPt15mWindowRef.current;
    if (previous == null) {
      lastFetchedPt15mWindowRef.current = openPt15mWindowStart;
      return undefined;
    }
    if (previous === openPt15mWindowStart) return undefined;
    lastFetchedPt15mWindowRef.current = openPt15mWindowStart;

    let active = true;
    void fetchOverviewSiteConditionPosture(siteId, {
      selectedOverTimeWindowId: overTimeWindowId,
    }).then((result) => {
      if (!active) return;
      applyOverviewFetchResult(result, setLoadState, siteId);
    }).catch(() => {
      if (!active) return;
      applyOverviewFetchResult({
        status: 'error',
        httpStatus: null,
        message: 'Condition-state data could not be loaded for this site.',
      }, setLoadState, siteId);
    });
    return () => {
      active = false;
    };
  }, [siteId, overTimeWindowId, openPt15mWindowStart]);

  useEffect(() => {
    let active = true;
    const summary = loadState.summary;
    const identities = identitiesFromSummary(summary);
    if (!summary || !identities.length) {
      setWorkflowIdentities([]);
      return () => {
        active = false;
      };
    }
    setWorkflowIdentities(identities);
    void reportsApi.listSafetyCases({ limit: 100 }).then(async (result) => {
      if (!active) return;
      const withCases = attachOverviewV2SafetyCaseIds(identities, result.safety_cases || result.items || []);
      setWorkflowIdentities(withCases);
      const withControls = await attachOverviewV2RecordedControls(
        withCases,
        (workflowId) => reportsApi.listWorkflowCorrectiveActions(workflowId),
      );
      if (!active) return;
      setWorkflowIdentities(withControls);
    }).catch(async () => {
      if (!active) return;
      setWorkflowIdentities(identities);
      try {
        const withControls = await attachOverviewV2RecordedControls(
          identities,
          (workflowId) => reportsApi.listWorkflowCorrectiveActions(workflowId),
        );
        if (!active) return;
        setWorkflowIdentities(withControls);
      } catch {
        if (!active) return;
        setWorkflowIdentities(identities);
      }
    });
    return () => {
      active = false;
    };
  }, [loadState.summary]);

  return (
    <Box sx={{ minWidth: 0, position: 'relative' }} data-testid="acceptance-overview-v2">
      <Stack spacing={0}>
        {loadState.status === 'loading' ? (
          <Box sx={{ position: 'absolute', zIndex: 2, top: 16, right: 16 }}>
            <Alert severity="info" icon={<CircularProgress size={18} />} data-testid="overview-v2-loading">
              Loading area data.
            </Alert>
          </Box>
        ) : null}

        {loadState.message ? (
          <Box sx={{ position: 'absolute', zIndex: 2, top: 16, right: 16, maxWidth: 420 }}>
            <Alert
              severity={loadState.status === 'error' || loadState.status === 'stale' ? 'warning' : 'info'}
              data-testid="overview-v2-load-message"
              action={loadState.status === 'error' || loadState.status === 'stale' ? (
                <Button
                  color="inherit"
                  size="small"
                  data-testid="overview-v2-retry"
                  onClick={() => setReloadNonce((value) => value + 1)}
                >
                  Retry
                </Button>
              ) : undefined}
            >
              {loadState.message}
            </Alert>
          </Box>
        ) : null}

        <OverviewV2AreaCentricView
          siteId={siteId}
          summary={loadState.summary}
          readState={loadState.readState}
          binding="live"
          workflowIdentities={workflowIdentities}
          selectedOverTimeWindowId={overTimeWindowId}
          onOverTimeWindowChange={setOverTimeWindowId}
          onNavigate={navigate}
        />
      </Stack>
    </Box>
  );
};

export default AcceptanceOverviewV2Page;

